import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  Receipt,
  Settings,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  CustomerMeter,
  Order,
  PendingPlanChange,
  Subscription,
} from "@/contexts/membership-context";
import { cn } from "@/lib/utils";
import type { MembershipTier, MembershipTierConfig } from "@/types/membership";
import { MembershipBadge, MembershipStatus } from "./membership-badge";
import { formatCurrency } from "./pricing-card";
import { UpgradeDialog } from "./upgrade-dialog";
import { UsageMetrics } from "./usage-metrics";

export type BillingDashboardProps = {
  subscriptions?: Subscription[];
  orders?: Order[];
  meters?: CustomerMeter[];
  currentTier: MembershipTier;
  tierConfig: MembershipTierConfig;
  onUpgrade?: (tierId: string, billingPeriod: "monthly" | "yearly") => Promise<void> | void;
  onManageBilling?: () => void;
  pendingPlanChange?: PendingPlanChange | null;
  onCancelPendingPlanChange?: (subscriptionId?: string) => Promise<void> | void;
  isCancelingPendingPlanChange?: boolean;
  isLoading?: boolean;
  className?: string;
};

type SubscriptionCardProps = {
  subscription: Subscription;
  onManage?: () => void;
  onUpgrade?: () => void;
};

function isUsableSubscription(subscription: Subscription): boolean {
  if (subscription.status === "active" || subscription.status === "trialing") {
    return true;
  }

  return (
    subscription.status === "canceled" &&
    Boolean(subscription.currentPeriodEnd && new Date(subscription.currentPeriodEnd) > new Date())
  );
}

function formatTierName(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatBillingCadence(cadence: "monthly" | "yearly"): string {
  return cadence === "yearly" ? "yearly" : "monthly";
}

function getPendingPlanChangeAction(planChange: PendingPlanChange): string {
  return planChange.direction === "cadence_change" ? "Billing cadence change" : "Plan change";
}

type PendingPlanChangeNoticeProps = {
  pendingPlanChange: PendingPlanChange;
  onCancel?: (subscriptionId?: string) => Promise<void> | void;
  isCanceling?: boolean;
};

function PendingPlanChangeNotice({
  pendingPlanChange,
  onCancel,
  isCanceling = false,
}: PendingPlanChangeNoticeProps) {
  const effectiveDate = new Date(pendingPlanChange.effectiveAt).toLocaleDateString();
  const targetTier = formatTierName(pendingPlanChange.targetTier);
  const targetCadence = formatBillingCadence(pendingPlanChange.targetBillingCadence);

  return (
    <Alert className="border-amber-300/60 bg-amber-50/70 dark:border-amber-900/60 dark:bg-amber-950/20">
      <Clock className="h-4 w-4 text-amber-700 dark:text-amber-300" />
      <AlertDescription className="flex flex-col gap-3 text-amber-950 dark:text-amber-100 sm:flex-row sm:items-center sm:justify-between">
        <span>
          {getPendingPlanChangeAction(pendingPlanChange)} scheduled: {targetTier} {targetCadence}{" "}
          starts on {effectiveDate}. Current Membership stays active until renewal.
        </span>
        {onCancel && (
          <Button
            className="shrink-0 border-amber-300 text-amber-950 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-100 dark:hover:bg-amber-950"
            disabled={isCanceling}
            onClick={() => {
              void onCancel(pendingPlanChange.subscriptionId);
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            <XCircle className="mr-1.5 h-3.5 w-3.5" />
            {isCanceling ? "Canceling..." : "Cancel Pending Plan change"}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

function SubscriptionCard({ subscription, onManage, onUpgrade }: SubscriptionCardProps) {
  const isActive = subscription.status === "active" || subscription.status === "trialing";
  const isCanceled = subscription.status === "canceled";
  const inGracePeriod =
    isCanceled &&
    subscription.currentPeriodEnd &&
    new Date(subscription.currentPeriodEnd) > new Date();

  const getStatusColor = () => {
    if (isActive) {
      return "default";
    }
    if (inGracePeriod) {
      return "secondary";
    }
    return "destructive";
  };

  const getStatusText = () => {
    if (isActive) {
      return "Active";
    }
    if (inGracePeriod && subscription.currentPeriodEnd) {
      return (
        "Canceled (Active until " +
        new Date(subscription.currentPeriodEnd).toLocaleDateString() +
        ")"
      );
    }
    return "Canceled";
  };

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">
              {subscription.metadata?.tier
                ? `${subscription.metadata.tier.toString().charAt(0).toUpperCase() + subscription.metadata.tier.toString().slice(1)} Plan`
                : "Subscription"}
            </CardTitle>
            <CardDescription>
              {subscription.metadata?.tier
                ? // Price information should be fetched from tier API or subscription data
                  "Active subscription"
                : "Unknown subscription"}
            </CardDescription>
          </div>
          <Badge variant={getStatusColor() as "default" | "secondary" | "destructive" | "outline"}>
            {getStatusText()}
          </Badge>
        </div>

        {inGracePeriod && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertDescription>
              Your subscription is canceled but remains active until{" "}
              {subscription.currentPeriodEnd
                ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                : "N/A"}
              . You can reactivate it anytime before then.
            </AlertDescription>
          </Alert>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-muted-foreground text-sm">Next billing</div>
            <div className="font-medium">
              {isActive && subscription.currentPeriodEnd
                ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                : "—"}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground text-sm">Started</div>
            <div className="font-medium">
              {subscription.createdAt ? new Date(subscription.createdAt).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex gap-2">
          {onManage && (
            <Button className="flex-1" onClick={onManage} size="sm" variant="outline">
              <Settings className="mr-1 h-3 w-3" />
              Manage
            </Button>
          )}
          {onUpgrade && (isActive || inGracePeriod) && (
            <Button className="flex-1" onClick={onUpgrade} size="sm">
              <ArrowUpRight className="mr-1 h-3 w-3" />
              Upgrade
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

type OrderHistoryProps = {
  orders: Order[];
};

// Helper function to get badge variant based on order status
function getOrderStatusVariant(status: string) {
  if (status === "completed") {
    return "default";
  }
  if (status === "pending") {
    return "secondary";
  }
  return "destructive";
}

// Helper function to get status icon
function getStatusIcon(status: string) {
  if (status === "completed") {
    return <CheckCircle className="h-4 w-4" />;
  }
  if (status === "pending") {
    return <Clock className="h-4 w-4" />;
  }
  if (status === "canceled" || status === "refunded") {
    return <AlertCircle className="h-4 w-4" />;
  }
  return null;
}

// Helper function to get status styling classes
function getStatusClasses(status: string) {
  const baseClasses = "flex h-8 w-8 items-center justify-center rounded-full";

  if (status === "completed") {
    return `${baseClasses} bg-green-100 text-green-600 dark:bg-green-900/20`;
  }
  if (status === "pending") {
    return `${baseClasses} bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20`;
  }
  if (status === "canceled" || status === "refunded") {
    return `${baseClasses} bg-red-100 text-red-600 dark:bg-red-900/20`;
  }
  return baseClasses;
}

function OrderHistory({ orders }: OrderHistoryProps) {
  if (orders.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Receipt className="mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">No orders found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <Card key={order.id}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <div className={getStatusClasses(order.status)}>{getStatusIcon(order.status)}</div>

              <div className="space-y-1">
                <div className="font-medium">
                  {order.metadata?.tier
                    ? `${order.metadata.tier.toString().charAt(0).toUpperCase() + order.metadata.tier.toString().slice(1)} Plan`
                    : "Order"}
                </div>
                <div className="text-muted-foreground text-sm">
                  {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "N/A"} •{" "}
                  {formatCurrency(order.totalAmount)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant={getOrderStatusVariant(order.status)}>
                {(order.status || "unknown").charAt(0).toUpperCase() +
                  (order.status || "unknown").slice(1)}
              </Badge>
              <Button size="sm" variant="ghost">
                <Eye className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost">
                <Download className="h-3 w-3" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function BillingDashboard({
  subscriptions = [],
  orders = [],
  meters = [],
  currentTier,
  tierConfig,
  onUpgrade,
  onManageBilling,
  pendingPlanChange,
  onCancelPendingPlanChange,
  isCancelingPendingPlanChange = false,
  isLoading = false,
  className,
}: BillingDashboardProps) {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const activeSubscription = (() => {
    const validSubs = subscriptions.filter(isUsableSubscription);

    if (validSubs.length > 1) {
      const nonDowngrade = validSubs.find((sub) => sub.metadata?.proratedDowngrade !== true);
      if (nonDowngrade) {
        return nonDowngrade;
      }
    }

    return validSubs.at(0);
  })();

  const handleUpgrade = (tierId: string, billingPeriod: "monthly" | "yearly") => {
    void onUpgrade?.(tierId, billingPeriod);
    setShowUpgradeDialog(false);
  };

  const handleManageBilling = () => {
    if (onManageBilling) {
      onManageBilling();
    } else {
      toast("There was an error managing your billing");
    }
  };

  return (
    <>
      <div className={cn("space-y-6", className)}>
        {/* Current Plan Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <CardTitle>Current Plan</CardTitle>
                <CardDescription>Manage your subscription and billing preferences</CardDescription>
              </div>
              <MembershipBadge size="lg" tier={currentTier} />
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {activeSubscription ? (
              <MembershipStatus
                expiresAt={
                  activeSubscription.currentPeriodEnd
                    ? new Date(activeSubscription.currentPeriodEnd)
                    : undefined
                }
                isActive={
                  activeSubscription.status === "active" || activeSubscription.status === "trialing"
                }
                pendingPlanChange={pendingPlanChange}
                tier={currentTier}
              />
            ) : (
              <div className="text-muted-foreground text-sm">No active subscription</div>
            )}

            {pendingPlanChange && (
              <PendingPlanChangeNotice
                isCanceling={isCancelingPendingPlanChange}
                onCancel={onCancelPendingPlanChange}
                pendingPlanChange={pendingPlanChange}
              />
            )}

            <div className="flex gap-2">
              <Button disabled={isLoading} onClick={handleManageBilling} variant="outline">
                <CreditCard className="mr-2 h-4 w-4" />
                Manage Billing
                <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
              <Button disabled={isLoading} onClick={() => setShowUpgradeDialog(true)}>
                <ArrowUpRight className="mr-2 h-4 w-4" />
                Change Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for detailed views */}
        <Tabs className="space-y-4" defaultValue="usage">
          <TabsList>
            <TabsTrigger value="usage">Usage & Limits</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="orders">Order History</TabsTrigger>
          </TabsList>

          <TabsContent value="usage">
            <UsageMetrics
              meters={meters}
              onUpgrade={() => setShowUpgradeDialog(true)}
              tierConfig={tierConfig}
            />
          </TabsContent>

          <TabsContent className="space-y-4" value="subscriptions">
            {subscriptions.length > 0 ? (
              subscriptions.map((subscription) => (
                <SubscriptionCard
                  key={subscription.id}
                  onManage={handleManageBilling}
                  onUpgrade={() => setShowUpgradeDialog(true)}
                  subscription={subscription}
                />
              ))
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <CreditCard className="mb-2 h-8 w-8 text-muted-foreground" />
                  <p className="mb-4 text-muted-foreground text-sm">No active subscriptions</p>
                  <Button onClick={() => setShowUpgradeDialog(true)}>Choose a Plan</Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="orders">
            <OrderHistory orders={orders} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Upgrade Dialog */}
      <UpgradeDialog
        currentTier={currentTier}
        isLoading={isLoading}
        onOpenChange={setShowUpgradeDialog}
        onUpgrade={handleUpgrade}
        open={showUpgradeDialog}
      />
    </>
  );
}
